/**
 * InfoTooltip Component - Redesigned for better UX
 * Provides contextual help tooltips with proper formatting, positioning, and readability
 */

import React, { useState, useRef, useEffect } from 'react';
import { Info, X, HelpCircle } from 'lucide-react';
import './tooltip-styles.css';

interface InfoTooltipProps {
  content: string;
  title?: string;
  className?: string;
  iconSize?: number;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
  variant?: 'default' | 'help' | 'info';
}

export const InfoTooltip: React.FC<InfoTooltipProps> = ({
  content,
  title,
  className = '',
  iconSize = 16,
  position = 'auto',
  variant = 'info',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<string>('bottom');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Calculate optimal position when tooltip opens
  useEffect(() => {
    if (isOpen && triggerRef.current && tooltipRef.current && position === 'auto') {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      // Check if there's space below
      const spaceBelow = viewportHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;
      const spaceRight = viewportWidth - triggerRect.right;
      const spaceLeft = triggerRect.left;

      // Determine best position (prioritize bottom, then top, then sides)
      if (spaceBelow >= 200) {
        setTooltipPosition('bottom');
      } else if (spaceAbove >= 200) {
        setTooltipPosition('top');
      } else if (spaceRight >= 400) {
        setTooltipPosition('right');
      } else if (spaceLeft >= 400) {
        setTooltipPosition('left');
      } else {
        setTooltipPosition('bottom'); // fallback
      }
    } else if (position !== 'auto') {
      setTooltipPosition(position);
    }
  }, [isOpen, position]);

  // Get icon based on variant
  const getIcon = () => {
    switch (variant) {
      case 'help':
        return <HelpCircle size={iconSize} />;
      case 'info':
      default:
        return <Info size={iconSize} />;
    }
  };

  // Get positioning classes
  const getPositionClasses = () => {
    const base = 'absolute z-50';
    switch (tooltipPosition) {
      case 'top':
        return `${base} bottom-full mb-3 left-1/2 transform -translate-x-1/2`;
      case 'bottom':
        return `${base} top-full mt-3 left-1/2 transform -translate-x-1/2`;
      case 'left':
        return `${base} right-full mr-3 top-1/2 transform -translate-y-1/2`;
      case 'right':
        return `${base} left-full ml-3 top-1/2 transform -translate-y-1/2`;
      default:
        return `${base} top-full mt-3 left-1/2 transform -translate-x-1/2`;
    }
  };

  // Get arrow classes
  const getArrowClasses = () => {
    const arrowBase = 'absolute w-0 h-0';
    switch (tooltipPosition) {
      case 'top':
        return {
          arrow: `${arrowBase} top-full left-1/2 transform -translate-x-1/2 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-white`,
          shadow: `${arrowBase} top-full left-1/2 transform -translate-x-1/2 translate-y-px border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-slate-200`,
        };
      case 'bottom':
        return {
          arrow: `${arrowBase} bottom-full left-1/2 transform -translate-x-1/2 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-white`,
          shadow: `${arrowBase} bottom-full left-1/2 transform -translate-x-1/2 -translate-y-px border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-slate-200`,
        };
      case 'left':
        return {
          arrow: `${arrowBase} left-full top-1/2 transform -translate-y-1/2 border-t-8 border-b-8 border-l-8 border-t-transparent border-b-transparent border-l-white`,
          shadow: `${arrowBase} left-full top-1/2 transform -translate-y-1/2 translate-x-px border-t-8 border-b-8 border-l-8 border-t-transparent border-b-transparent border-l-slate-200`,
        };
      case 'right':
        return {
          arrow: `${arrowBase} right-full top-1/2 transform -translate-y-1/2 border-t-8 border-b-8 border-r-8 border-t-transparent border-b-transparent border-r-white`,
          shadow: `${arrowBase} right-full top-1/2 transform -translate-y-1/2 -translate-x-px border-t-8 border-b-8 border-r-8 border-t-transparent border-b-transparent border-r-slate-200`,
        };
      default:
        return {
          arrow: '',
          shadow: '',
        };
    }
  };

  const arrowClasses = getArrowClasses();

  // Format content with better structure and markdown-like parsing
  const formatContent = (text: string) => {
    // Split by double newlines for paragraphs
    const paragraphs = text.split('\n\n');

    return paragraphs.map((paragraph, index) => {
      // Check for markdown-style bold headers (**text**)
      if (paragraph.includes('**') && paragraph.includes(':\n•')) {
        const lines = paragraph.split('\n');
        const headerMatch = lines[0].match(/\*\*(.*?)\*\*/);
        const header = headerMatch ? headerMatch[1] : lines[0];
        const listItems = lines.slice(1).filter((line) => line.trim() && line.startsWith('•'));

        return (
          <div key={index} className={index > 0 ? 'mt-4' : ''}>
            <div className="font-semibold text-blue-700 mb-2" style={{ color: '#1d4ed8' }}>
              {header}
            </div>
            <ul className="space-y-1.5">
              {listItems.map((item, i) => (
                <li key={i} className="flex items-start">
                  <span className="text-blue-500 mr-2 mt-0.5 font-bold">•</span>
                  <span className="leading-snug" style={{ color: '#374151' }}>
                    {item.replace(/^•\s*/, '').trim()}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        );
      }
      // Check if it's a regular list (starts with bullet points)
      else if (paragraph.includes('\n•') || paragraph.includes('\n-')) {
        const lines = paragraph.split('\n');
        const title = lines[0];
        const listItems = lines.slice(1).filter((line) => line.trim());

        return (
          <div key={index} className={index > 0 ? 'mt-3' : ''}>
            {title && (
              <div className="font-medium mb-2" style={{ color: '#1f2937' }}>
                {title}
              </div>
            )}
            <ul className="space-y-1">
              {listItems.map((item, i) => (
                <li key={i} className="flex items-start">
                  <span className="text-blue-500 mr-2 mt-0.5">•</span>
                  <span style={{ color: '#374151' }}>{item.replace(/^[•\-]\s*/, '').trim()}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      } else {
        // Regular paragraph - handle italic text in markdown
        let formattedText = paragraph.trim();

        // Convert *italic* to italic styling
        const italicRegex = /\*(.*?)\*/g;
        const hasItalic = italicRegex.test(formattedText);

        if (hasItalic) {
          return (
            <p
              key={index}
              className={`leading-relaxed italic text-slate-600 ${index > 0 ? 'mt-3' : ''}`}
              style={{ color: '#64748b' }}
            >
              {formattedText.replace(/\*(.*?)\*/g, '$1')}
            </p>
          );
        }

        return (
          <p
            key={index}
            className={`leading-relaxed ${index > 0 ? 'mt-3' : ''}`}
            style={{ color: '#374151' }}
          >
            {formattedText}
          </p>
        );
      }
    });
  };

  return (
    <div className="relative inline-block">
      {/* Trigger Icon */}
      <button
        ref={triggerRef}
        type="button"
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setTimeout(() => setIsOpen(false), 150)}
        className={`tooltip-trigger inline-flex items-center justify-center text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-full p-1.5 transition-all duration-200 ${className}`}
        aria-label="More information"
        aria-expanded={isOpen}
        aria-describedby={isOpen ? 'tooltip-content' : undefined}
      >
        {getIcon()}
      </button>

      {/* Tooltip Portal for better positioning */}
      {isOpen && (
        <>
          {/* Mobile Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/20 backdrop-blur-xs sm:hidden"
            onClick={() => setIsOpen(false)}
          />

          {/* Mobile Modal */}
          <div className="fixed inset-x-4 top-1/2 transform -translate-y-1/2 z-50 sm:hidden">
            <div
              className="tooltip-mobile bg-white rounded-2xl shadow-2xl border border-slate-200 max-h-96 overflow-y-auto"
              style={{ backgroundColor: 'white', borderColor: '#e2e8f0' }}
            >
              {/* Mobile Header */}
              <div className="flex items-center justify-between p-4 border-b border-slate-100">
                {title && (
                  <h4 className="text-lg font-semibold pr-4" style={{ color: '#111827' }}>
                    {title}
                  </h4>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="shrink-0 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Mobile Content */}
              <div className="p-4">
                <div className="text-sm leading-relaxed" style={{ color: '#374151' }}>
                  {formatContent(content)}
                </div>
              </div>
            </div>
          </div>

          {/* Desktop Tooltip */}
          <div
            ref={tooltipRef}
            id="tooltip-content"
            className={`hidden sm:block ${getPositionClasses()} tooltip-container ${tooltipPosition ? `position-${tooltipPosition}` : ''}`}
            role="tooltip"
            style={{
              width: '400px',
              maxWidth: '90vw',
              zIndex: 9999,
            }}
          >
            <div
              className="tooltip-content bg-white rounded-xl shadow-2xl border border-slate-200 p-6 relative"
              style={{ backgroundColor: 'white', borderColor: '#e2e8f0' }}
            >
              {/* Arrow */}
              <div className={arrowClasses.shadow}></div>
              <div className={arrowClasses.arrow}></div>

              {/* Content */}
              <div>
                {title && (
                  <h4
                    className="tooltip-title text-lg font-semibold mb-4 leading-tight"
                    style={{ color: '#111827' }}
                  >
                    {title}
                  </h4>
                )}

                <div className="tooltip-text text-sm leading-relaxed" style={{ color: '#374151' }}>
                  {formatContent(content)}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default InfoTooltip;
